import { PlaybookStatus } from '@driftt/api-types'
import {
  Flex,
  Icon,
  IconButton,
  InfoBadge,
  Link,
  StatusBadge,
  Text,
  Tooltip,
} from '@driftt/dds'
import { faCalendar } from '@driftt/dds.icons/regular/faCalendar'
import { faChartColumn } from '@driftt/dds.icons/regular/faChartColumn'
import moment from 'moment'
import { isEmpty, pathOr, without } from 'ramda'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { getArchivedBlocksUsedInPlaybook } from 'api/blocks'
import {
  checkCanClonePlaybookAsBlock,
  updateArchiveFlag,
  updateRestoreFlag,
} from 'api/playbooks'
import { AISparklesTooltip } from 'components/AISparklesTooltip'
import {
  PLAYBOOK_ACTIONS,
  PLAYBOOK_INTERACTION_MODAL,
  TOAST_NOTIFICATIONS,
} from 'components/PlaybooksTable/constants'
import { getPlaybookReportPath } from 'components/PlaybooksTable/helpers'
import config from 'config'
import { TIMEZONE_TO_ABBR } from 'constants/timezones'
import {
  CLP_AS_PLAYBOOK_RELEASE_TIME,
  CLP_PLAYBOOK_TYPE,
  LANDING_PAGE_PLAYBOOK_REPORT_TYPES,
  REPORT_TYPE_TO_TABLE_NAME,
} from 'constants/types'
import { DEFAULT_WORKSPACE } from 'constants/workspaces'
import {
  getWorkspacesWithWritablePlaybooks,
  userHasPlaybookReports,
  userHasReusableBlocksV2,
} from 'utils/access'
import { isEmptyOrNil } from 'utils/index'
import {
  isEmailPlaybook,
  isFastLanePlaybook,
  isNewEditorPlaybook,
  isOneTimeEmailPlaybook,
  isValidPlaybookToCloneAsBlock,
} from 'utils/playbooks'

import { ActionMenu } from '../index'

const noop = () => {}

function isEnabledOrDisabled(status) {
  return ['ENABLED', 'DISABLED'].includes(status)
}

export const checkForArchivedBlocksOnRestore = (
  callableActions,
  setPlaybookRowModalContext,
  playbook
) => {
  if (!isNewEditorPlaybook(playbook.reportType)) {
    return callableActions('RESTORE')
  }
  getArchivedBlocksUsedInPlaybook(playbook.id)
    .then(({ data }) => {
      if (!!data && !isEmpty(data)) {
        setPlaybookRowModalContext({
          [PLAYBOOK_INTERACTION_MODAL.RESTORE_ARCHIVED_BLOCKS]: {
            playbook,
            archivedBlocks: data,
            onRestore: () => callableActions('RESTORE'),
          },
        })
      } else {
        callableActions('RESTORE')
      }
    })
    .catch((e) => {
      console.warn(e)
    })
}

function actionsGenerator({
  id,
  callback,
  name,
  reportType,
  showTransientNotification,
}) {
  return async function (type = null, replaceCurrentWindow = false) {
    try {
      if (type === 'ARCHIVE') {
        try {
          await updateArchiveFlag(id)
        } catch (err) {
          console.warn('Failed to update archive flag', err)
        }
      }

      if (type === 'RESTORE') {
        try {
          await updateRestoreFlag(id)
        } catch (err) {
          console.warn('Failed to update restore flag', err)
        }
      }
      const functionToCallWithId = PLAYBOOK_ACTIONS[type] || noop
      const { data } = await functionToCallWithId(id)

      if (replaceCurrentWindow) {
        if (isNewEditorPlaybook(reportType)) {
          window.location.pathname = `/playbooks/editor/${data.id}`
        } else {
          window.location.href = `${config.CUSTOMER_WEB_URL}/playbooks/${data.id}/edit`
        }
      } else {
        const makeAToast = TOAST_NOTIFICATIONS[type]
          ? TOAST_NOTIFICATIONS[type](name, data.id)
          : 'Successfully updated'

        showTransientNotification({ message: makeAToast, type: 'success' })
        callback()
      }
    } catch (err) {
      let errorMessage

      // cases for restricted functionality on sunset email features
      const responseText = pathOr('', ['response', 'data', 'message'], err)
      switch (responseText) {
        case 'org_cannot_enable_sales_sequence_playbooks':
        case 'org_cannot_enable_marketing_email_playbooks':
          errorMessage = 'This playbook type can no longer be enabled'
          break

        case 'org_cannot_clone_sales_sequence_playbooks':
        case 'org_cannot_clone_marketing_email_playbooks':
          errorMessage = 'Cloning has been disabled for this playbook type'
          break

        default:
          errorMessage = 'Could not save changes'
          break
      }

      showTransientNotification({
        message: errorMessage,
        type: 'error',
      })
      console.warn(err)
    }
  }
}

export function PlaybookPriorityCell(playbook) {
  return (
    <Text variant="bodyBold" css={{ textAlign: 'center' }}>
      {playbook?.meta?.virtualPriority ?? playbook?.priority}
    </Text>
  )
}

export function PlaybookNameCell(playbook) {
  const isPlaybookUsingAI = useMemo(
    () => playbook?.meta?.aiEnabled || playbook?.meta?.isGPTEnabled,
    [playbook]
  )
  return (
    <Flex direction="row">
      {isPlaybookUsingAI ? (
        <AISparklesTooltip
          conditions={[
            { name: 'aiEnabled', enabled: playbook?.meta?.aiEnabled },
            { name: 'isGPTEnabled', enabled: playbook?.meta?.isGPTEnabled },
          ]}
        />
      ) : null}
      <Link
        href={`${
          isEmailPlaybook(playbook.reportType)
            ? `/playbooks/${playbook.id}/edit`
            : `/playbooks/editor/${playbook.id}`
        }`}
        target="_blank"
        variant="object"
        rel="noopener noreferrer"
      >
        {playbook.name}
      </Link>
    </Flex>
  )
}

const STATUS_VARIANTS = Object.freeze({
  [PlaybookStatus.ARCHIVED]: 'archive',
  [PlaybookStatus.ENABLED]: 'success',
  [PlaybookStatus.DELETED]: 'error',
  [PlaybookStatus.DISABLED]: 'error',
  [PlaybookStatus.SCHEDULED]: 'error',
  [PlaybookStatus.SENT]: 'error',
})

export function PlaybookStatusCell({
  status,
  targeting,
  experiment,
  scheduledTime,
  timeZone,
}) {
  const { rolloutPercent } = targeting ?? {}
  const isPlaybookOn = status === PlaybookStatus.ENABLED
  const isPlaybookArchived = status === PlaybookStatus.ARCHIVED
  const { enabled: isExperimentEnabled } = experiment ?? {}
  const showRolloutPercent = isPlaybookOn
    ? rolloutPercent && rolloutPercent < 100
    : false

  return (
    <Flex direction="row" gap={1} alignItems="center">
      <Flex direction="row" alignItems="center">
        {isPlaybookArchived ? (
          <InfoBadge variant="primary" size="small">
            {PlaybookStatus.ARCHIVED}
          </InfoBadge>
        ) : (
          <StatusBadge size="small" variant={STATUS_VARIANTS[status]}>
            {isPlaybookOn
              ? `ON${showRolloutPercent ? ` (${rolloutPercent}%)` : ''}`
              : 'OFF'}
          </StatusBadge>
        )}
      </Flex>
      {isPlaybookOn && isExperimentEnabled && (
        <StatusBadge variant="success" size="small">
          A/B
        </StatusBadge>
      )}
      {scheduledTime && timeZone && (
        <Tooltip
          side="up"
          content={`Playbook is scheduled to be disabled on ${moment(
            scheduledTime
          ).format('MM/DD/YYYY [at] h:MM A')} ${TIMEZONE_TO_ABBR[timeZone]}`}
        >
          <Icon icon={faCalendar} height={14} width={14} />
        </Tooltip>
      )}
    </Flex>
  )
}

export function PlaybookReportTypeCell({ createdAt, reportType }) {
  const hasCLPTag =
    createdAt > CLP_AS_PLAYBOOK_RELEASE_TIME &&
    LANDING_PAGE_PLAYBOOK_REPORT_TYPES.includes(reportType)

  const playbookType = hasCLPTag
    ? CLP_PLAYBOOK_TYPE
    : REPORT_TYPE_TO_TABLE_NAME[reportType]

  return playbookType
}

export function PlaybookUpdatedAuthorCell(
  { updatedAuthorId, componentOrgUsers = {} },
  orgUsers
) {
  const user =
    orgUsers[updatedAuthorId] || componentOrgUsers[updatedAuthorId] || {}
  return user.name || user.email || 'teammate'
}

export function PlaybookActionsCell({
  callback = noop,
  canEnableOrDisablePlaybook,
  enabledPlaybookReportTypes,
  playbook,
  showTransientNotification,
  setPlaybookRowModalContext = noop,
}) {
  const {
    id,
    name,
    reportType,
    status,
    workspaceId = DEFAULT_WORKSPACE.id,
  } = playbook

  const callableActions = actionsGenerator({
    id,
    callback,
    name,
    reportType,
    showTransientNotification,
  })
  const isArchived = status === 'ARCHIVED'
  const isEmail = isEmailPlaybook(reportType)
  const isOutboundChatPlaybook =
    reportType === 'TALK_TO_YOUR_OUTBOUND_PROSPECTS'
  const isSignaturePlaybook = reportType === 'SIGNATURE'
  const isScheduledPlaybook = status === 'SCHEDULED'
  const isUpgradeLocked = isArchived && !enabledPlaybookReportTypes[reportType]
  const isOneTimeEmail = isOneTimeEmailPlaybook(reportType)
  const isFastlane = isFastLanePlaybook(playbook)
  const canAccessReports = userHasPlaybookReports() && !isSignaturePlaybook

  const writableWorkspaceIds = getWorkspacesWithWritablePlaybooks(playbook)
  const canWriteToWorkspace = writableWorkspaceIds.includes(workspaceId)

  const canMovePlaybook =
    !isEmail &&
    without([workspaceId], writableWorkspaceIds).length &&
    canWriteToWorkspace

  const canClonePlaybook =
    (isEmail || writableWorkspaceIds.length) && !isFastlane

  const goToReport = (e) => {
    e.stopPropagation()
    window.location.pathname = getPlaybookReportPath(playbook)
  }

  const [cloneAsBlockExclusions, setCloneAsBlockExclusions] = useState({})
  const [playbookRowActions, setPlaybookRowActions] = useState([])

  const activeDefaults = [
    {
      icon: 'inbox-full',
      label: 'Archive',
      fn: () => callableActions('ARCHIVE'),
    },
  ]

  if (isEnabledOrDisabled(status) && canEnableOrDisablePlaybook) {
    const dropDownProperties = {
      ENABLED: {
        label: 'Turn off playbook',
        fn: () => callableActions('DISABLE'),
      },
      DISABLED: {
        label: 'Turn on playbook',
        fn: () => callableActions('ENABLE'),
      },
    }

    activeDefaults.unshift(dropDownProperties[status] || {})
  }

  const inactiveDefaults = [
    isUpgradeLocked
      ? {
          label: 'Upgrade to Restore',
          fn: () => window.history.pushState({}, '', '/settings/billing'),
        }
      : {
          icon: 'inbox-empty',
          label: 'Restore',
          fn: () =>
            checkForArchivedBlocksOnRestore(
              callableActions,
              setPlaybookRowModalContext,
              playbook
            ),
        },
    {
      icon: 'trash',
      label: 'Delete',
      fn: () =>
        setPlaybookRowModalContext({
          [PLAYBOOK_INTERACTION_MODAL.DELETE]: { playbook },
        }),
    },
  ]

  const actions = useCallback(() => {
    if (isScheduledPlaybook || isSignaturePlaybook || isOneTimeEmail) {
      if (!isArchived) {
        return activeDefaults
      } else {
        return inactiveDefaults
      }
    }

    if (isOutboundChatPlaybook) {
      if (!isArchived) {
        activeDefaults.push({
          label: 'Cloning is disabled for this playbook type.',
          fn: noop,
          disabled: true,
        })
        return activeDefaults
      } else {
        return inactiveDefaults
      }
    }

    if (!isArchived) {
      if (canMovePlaybook) {
        activeDefaults.unshift({
          label: 'Move to another workspace',
          fn: () =>
            setPlaybookRowModalContext({
              [PLAYBOOK_INTERACTION_MODAL.MOVE]: { playbook },
            }),
        })
      }

      if (canClonePlaybook) {
        const hasActiveExperiment = pathOr(
          false,
          ['experiment', 'enabled'],
          playbook
        )
        if (isValidPlaybookToCloneAsBlock(playbook.reportType)) {
          const isAiExclusion = pathOr(
            false,
            ['aiExclusion'],
            cloneAsBlockExclusions
          )
          const isAiTopicExclusion = pathOr(
            false,
            ['aiTopicExclusion'],
            cloneAsBlockExclusions
          )
          const isBlockExclusion = pathOr(
            false,
            ['blockExclusion'],
            cloneAsBlockExclusions
          )
          const hasExclusions = pathOr(
            false,
            ['hasExclusions'],
            cloneAsBlockExclusions
          )

          activeDefaults.unshift({
            label:
              isAiExclusion ||
              isBlockExclusion ||
              isAiTopicExclusion ||
              hasActiveExperiment
                ? `Cloning restricted: ${
                    isBlockExclusion ? 'Reusable block in use' : ''
                  }${isAiTopicExclusion ? 'Ai Topics in use' : ''}${
                    isAiExclusion ? 'Ai is in use' : ''
                  }${hasActiveExperiment ? 'A/B test in progress' : ''}`
                : 'Clone as reusable block',
            disabled:
              hasExclusions ||
              !userHasReusableBlocksV2() ||
              hasActiveExperiment,
            fn: () =>
              setPlaybookRowModalContext({
                [PLAYBOOK_INTERACTION_MODAL.CLONE_AS_BLOCK]: { playbook },
              }),
          })
          activeDefaults.unshift({
            label: hasActiveExperiment
              ? 'Cloning restricted: A/B test in progress'
              : 'Clone as landing page',
            disabled: hasActiveExperiment,
            fn: () =>
              setPlaybookRowModalContext({
                [PLAYBOOK_INTERACTION_MODAL.CLONE_AS_CLP]: { playbook },
              }),
          })
          activeDefaults.unshift({
            label: hasActiveExperiment
              ? 'Cloning restricted: A/B test in progress'
              : 'Clone as playbook',
            disabled: hasActiveExperiment,
            fn: () =>
              setPlaybookRowModalContext({
                [PLAYBOOK_INTERACTION_MODAL.CLONE_AS_BOT]: { playbook },
              }),
          })
        } else {
          activeDefaults.unshift({
            label: hasActiveExperiment
              ? 'Cloning restricted: A/B test in progress'
              : 'Clone',
            disabled: hasActiveExperiment,
            fn: () =>
              setPlaybookRowModalContext({
                [PLAYBOOK_INTERACTION_MODAL.CLONE]: { playbook },
              }),
          })
        }
      }

      return activeDefaults
    } else {
      return inactiveDefaults
    }
  }, [
    activeDefaults,
    canClonePlaybook,
    canMovePlaybook,
    cloneAsBlockExclusions,
    inactiveDefaults,
    isArchived,
    isOneTimeEmail,
    isOutboundChatPlaybook,
    isScheduledPlaybook,
    isSignaturePlaybook,
    playbook,
    setPlaybookRowModalContext,
  ])

  useEffect(() => {
    const getAndFormatCloneErrors = async (pbId) => {
      try {
        const { data: blockCloneErrors } = await checkCanClonePlaybookAsBlock(
          pbId
        )
        return blockCloneErrors
      } catch (e) {
        console.warn(e)
      }
    }
    if (isEmptyOrNil(cloneAsBlockExclusions) && !isEmptyOrNil(playbook)) {
      getAndFormatCloneErrors(playbook.id).then((data) => {
        setCloneAsBlockExclusions(data)
      })
    }
  }, [cloneAsBlockExclusions, playbook])

  useEffect(() => {
    if (
      !isEmptyOrNil(cloneAsBlockExclusions) &&
      isEmptyOrNil(playbookRowActions)
    ) {
      setPlaybookRowActions(actions())
    }
  }, [actions, cloneAsBlockExclusions, playbookRowActions])

  return (
    <Flex direction="row">
      {canAccessReports && (
        <Tooltip content="View report" side="top">
          <IconButton icon={faChartColumn} onClick={goToReport} />
        </Tooltip>
      )}
      {!isEmptyOrNil(cloneAsBlockExclusions) && (
        <ActionMenu
          actions={playbookRowActions}
          playbook={playbook}
          setCloneAsBlockExclusions={setCloneAsBlockExclusions}
        />
      )}
    </Flex>
  )
}
